/**
 * Alert Engine - silnik ewaluacji reguł alertów dla zamówień
 */

import type { OrderRecord } from '../types/orders';
import type { AlertRule, AlertCondition, AlertResult, AlertOperator } from '../types/orderAlerts';

/**
 * Sprawdza czy pojedynczy warunek jest spełniony dla danego zamówienia
 */
export function evaluateCondition(order: OrderRecord, condition: AlertCondition): boolean {
  const { field, operator, value, compareToField } = condition;
  const fieldValue = (order as any)[field];

  switch (operator) {
    case 'isEmpty':
      return fieldValue === null || fieldValue === undefined || fieldValue === '';

    case 'isNotEmpty':
      return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    case 'equals':
      return fieldValue === value;

    case 'notEquals':
      return fieldValue !== value;

    case 'before':
      if (!fieldValue || !value) return false;
      return new Date(fieldValue) < new Date(value as string);

    case 'after':
      if (!fieldValue || !value) return false;
      return new Date(fieldValue) > new Date(value as string);

    case 'beforeToday':
      if (!fieldValue) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const fieldDate = new Date(fieldValue);
      fieldDate.setHours(0, 0, 0, 0);
      return fieldDate < today;

    case 'afterToday':
      if (!fieldValue) return false;
      const todayAfter = new Date();
      todayAfter.setHours(0, 0, 0, 0);
      const fieldDateAfter = new Date(fieldValue);
      fieldDateAfter.setHours(0, 0, 0, 0);
      return fieldDateAfter > todayAfter;

    case 'isToday':
      if (!fieldValue) return false;
      const todayIs = new Date();
      todayIs.setHours(0, 0, 0, 0);
      const fieldDateIs = new Date(fieldValue);
      fieldDateIs.setHours(0, 0, 0, 0);
      return fieldDateIs.getTime() === todayIs.getTime();

    case 'daysAgo':
      // Sprawdza czy data była X lub więcej dni temu
      if (!fieldValue || value === null || value === undefined) return false;
      const todayAgo = new Date();
      todayAgo.setHours(0, 0, 0, 0);
      const fieldDateAgo = new Date(fieldValue);
      fieldDateAgo.setHours(0, 0, 0, 0);
      const diffDaysAgo = Math.floor((todayAgo.getTime() - fieldDateAgo.getTime()) / (1000 * 60 * 60 * 24));
      return diffDaysAgo >= (value as number);

    case 'daysFromNow':
      // Sprawdza czy data jest za X lub mniej dni
      if (!fieldValue || value === null || value === undefined) return false;
      const todayFrom = new Date();
      todayFrom.setHours(0, 0, 0, 0);
      const fieldDateFrom = new Date(fieldValue);
      fieldDateFrom.setHours(0, 0, 0, 0);
      const diffDaysFrom = Math.floor((fieldDateFrom.getTime() - todayFrom.getTime()) / (1000 * 60 * 60 * 24));
      return diffDaysFrom <= (value as number) && diffDaysFrom >= 0;

    case 'olderThan':
      // Sprawdza różnicę między dwiema datami (np. akceptacja była, ale produkcja nie przez >X dni)
      if (!fieldValue || !compareToField || value === null || value === undefined) return false;
      const compareValue = (order as any)[compareToField];
      if (!compareValue) return false;
      const date1 = new Date(fieldValue);
      const date2 = new Date(compareValue);
      const diffDays = Math.abs(Math.floor((date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24)));
      return diffDays > (value as number);

    case 'fieldComparison':
      // Porównanie dwóch pól
      if (!compareToField) return false;
      const compareFieldValue = (order as any)[compareToField];
      if (!fieldValue || !compareFieldValue) return false;
      return new Date(fieldValue) < new Date(compareFieldValue);

    default:
      console.warn(`Unknown operator: ${operator}`);
      return false;
  }
}

/**
 * Sprawdza czy zamówienie spełnia wszystkie warunki reguły
 */
export function evaluateRule(order: OrderRecord, rule: AlertRule): AlertResult | null {
  if (!rule.enabled) return null;

  const logic = rule.conditionLogic || 'AND';
  const results = rule.conditions.map(condition => evaluateCondition(order, condition));
  const matchedCount = results.filter(Boolean).length;

  let matches = false;
  if (logic === 'AND') {
    matches = results.every(Boolean);
  } else if (logic === 'OR') {
    matches = results.some(Boolean);
  }

  if (!matches) return null;

  return {
    ruleId: rule.id,
    ruleName: rule.name,
    color: rule.color,
    priority: rule.priority,
    matchedConditions: matchedCount,
    totalConditions: rule.conditions.length
  };
}

/**
 * Sprawdza wszystkie reguły dla zamówienia i zwraca najważniejszy alert
 * (reguła z najwyższym priorytetem)
 */
export function evaluateAllRules(order: OrderRecord, rules: AlertRule[]): AlertResult | null {
  const matches: AlertResult[] = [];

  for (const rule of rules) {
    const result = evaluateRule(order, rule);
    if (result) {
      matches.push(result);
    }
  }

  if (matches.length === 0) return null;

  // Sortuj po priorytecie (malejąco) i zwróć pierwszy
  matches.sort((a, b) => b.priority - a.priority);
  return matches[0];
}

/**
 * Zwraca wszystkie pasujące alerty dla zamówienia (nie tylko najważniejszy)
 */
export function evaluateAllRulesMultiple(order: OrderRecord, rules: AlertRule[]): AlertResult[] {
  const matches: AlertResult[] = [];

  for (const rule of rules) {
    const result = evaluateRule(order, rule);
    if (result) {
      matches.push(result);
    }
  }

  // Sortuj po priorytecie (malejąco)
  matches.sort((a, b) => b.priority - a.priority);
  return matches;
}

/**
 * Helper: formatuje opis operatora dla UI
 */
export function getOperatorLabel(operator: AlertOperator): string {
  const labels: Record<AlertOperator, string> = {
    isEmpty: 'jest puste',
    isNotEmpty: 'jest wypełnione',
    equals: 'równa się',
    notEquals: 'nie równa się',
    before: 'przed datą',
    after: 'po dacie',
    beforeToday: 'przed dzisiaj',
    afterToday: 'po dzisiaj',
    isToday: 'jest dzisiaj',
    daysAgo: 'było X lub więcej dni temu',
    daysFromNow: 'za X lub mniej dni',
    olderThan: 'różnica > X dni',
    fieldComparison: 'porównanie z innym polem'
  };
  return labels[operator] || operator;
}

/**
 * Helper: formatuje nazwę pola dla UI
 */
export function getFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    order_nr: 'Numer zamówienia',
    client_po: 'Nr PO klienta',
    order_date: 'Data zamówienia',
    ref_name: 'Nazwa ref.',
    responsible: 'Handlowiec',
    product_short_description: 'Opis produktu',
    drawings_planned_date: 'Plan rysunków',
    drawing_approval_date: 'Akceptacja rysunków',
    in_production_since: 'Data produkcji',
    requested_delivery_week: 'Tydzień dostawy',
    proposed_lead_time_collection: 'Plan załadunku',
    confirmed_collection_date: 'Potw. załadunek',
    delivery_mode: 'Rodzaj dostawy',
    estimated_delivery_date: 'Szac. dostawa',
    remarks_on_montage: 'Uwagi montaż',
    delivery_list_no: 'Nr listu',
    delivery_address: 'Adres dostawy',
    onsite_contact_person: 'Osoba kontaktowa',
    special_requirements: 'Wymogi specjalne',
    real_delivery_date: 'Realna dostawa'
  };
  return labels[field] || field;
}

/**
 * Helper: lista wszystkich dostępnych pól do wyboru w konfiguratorze
 */
export const AVAILABLE_FIELDS = [
  { value: 'order_date', label: 'Data zamówienia' },
  { value: 'drawings_planned_date', label: 'Plan rysunków' },
  { value: 'drawing_approval_date', label: 'Akceptacja rysunków' },
  { value: 'in_production_since', label: 'Data produkcji' },
  { value: 'proposed_lead_time_collection', label: 'Plan załadunku' },
  { value: 'confirmed_collection_date', label: 'Potw. załadunek' },
  { value: 'estimated_delivery_date', label: 'Szac. dostawa' },
  { value: 'real_delivery_date', label: 'Realna dostawa' },
  { value: 'responsible', label: 'Handlowiec' },
  { value: 'delivery_mode', label: 'Rodzaj dostawy' },
  { value: 'ref_name', label: 'Nazwa ref.' },
  { value: 'product_short_description', label: 'Opis produktu' }
];

/**
 * Helper: lista operatorów z opisami
 */
export const AVAILABLE_OPERATORS: Array<{ value: AlertOperator; label: string; requiresValue: boolean }> = [
  { value: 'isEmpty', label: 'jest puste', requiresValue: false },
  { value: 'isNotEmpty', label: 'jest wypełnione', requiresValue: false },
  { value: 'equals', label: 'równa się', requiresValue: true },
  { value: 'notEquals', label: 'nie równa się', requiresValue: true },
  { value: 'beforeToday', label: 'przed dzisiaj (data)', requiresValue: false },
  { value: 'afterToday', label: 'po dzisiaj (data)', requiresValue: false },
  { value: 'isToday', label: 'jest dzisiaj (data)', requiresValue: false },
  { value: 'daysAgo', label: 'było X lub więcej dni temu', requiresValue: true },
  { value: 'daysFromNow', label: 'za X lub mniej dni', requiresValue: true },
  { value: 'before', label: 'przed datą (konkretną)', requiresValue: true },
  { value: 'after', label: 'po dacie (konkretną)', requiresValue: true }
];
